import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Nav } from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { buildShoppingList } from "../lib/affiliate";
import { FeedbackCard } from "../components/FeedbackCard";
import {
  ArrowLeft, Trash2, Download, Sparkles, ShoppingBag, ExternalLink,
  Crown, Loader2, Languages, Lock,
} from "lucide-react";

const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "hindi", label: "हिन्दी" },
  { id: "tamil", label: "தமிழ்" },
  { id: "telugu", label: "తెలుగు" },
  { id: "bengali", label: "বাংলা" },
  { id: "marathi", label: "मराठी" },
];

export default function LookDetail() {
  const { id } = useParams();
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState("en");
  const [working, setWorking] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = user?.tier === "premium";
  const isBasicOrPremium = user?.tier === "basic" || user?.tier === "premium";
  const tasteUnlock = !!look?.is_premium_taste;
  const canTranslate = isBasicOrPremium || tasteUnlock;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/looks/${id}`);
        setLook(data);
      } catch {
        toast.error("Could not load this look");
        navigate("/gallery");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const remove = async () => {
    if (!window.confirm("Delete this look?")) return;
    try {
      await api.delete(`/looks/${id}`);
      toast.success("Look removed");
      navigate("/gallery");
    } catch {
      toast.error("Could not delete");
    }
  };

  const download = () => {
    if (!isBasicOrPremium && !tasteUnlock) {
      toast.error("HD download is a Basic+ feature");
      navigate("/subscription");
      return;
    }
    const src = activePreview();
    if (!src) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${src}`;
    a.download = `styleme-${look.occasion.toLowerCase()}.png`;
    a.click();
  };

  // Both hairstyle try-on and color swap features were removed Feb 2026.
  // Backend endpoints `/looks/{id}/hairstyle` and `/looks/{id}/color` still exist
  // (kept for forward-compatibility) but are no longer called from the UI.

  const translate = async (lang) => {
    if (lang === "en") {
      setLanguage("en");
      return;
    }
    if (!canTranslate) {
      toast.error("Translation is a Basic+ feature");
      navigate("/subscription");
      return;
    }
    if (look?.translations?.[lang]) {
      setLanguage(lang);
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post(`/looks/${id}/translate`, { language: lang });
      setLook(data);
      setLanguage(lang);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Translation failed");
    } finally {
      setWorking(false);
    }
  };

  const activePreview = () => {
    if (!look) return null;
    return look.preview_image_base64 || look.original_image_base64;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0E13]">
        <Nav />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="aspect-[4/5] shimmer rounded-sm" />
            <div className="space-y-4">
              <div className="h-8 shimmer rounded-sm w-2/3" />
              <div className="h-32 shimmer rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!look) return null;

  // Display data (translated or original)
  const view = (language !== "en" && look.translations?.[language]) ? look.translations[language] : {
    summary: look.summary,
    outfit: look.outfit,
    makeup: look.makeup,
    hairstyle: look.hairstyle,
    accessories: look.accessories,
    confidence_tips: look.confidence_tips,
  };
  const previewSrc = activePreview();

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 sm:py-12">
        {tasteUnlock && !isPremium && (
          <div data-testid="premium-taste-banner" className="mb-8 border border-[#E3C2B0] bg-[#E3C2B0]/10 rounded-sm px-6 py-4 flex items-center justify-between flex-wrap gap-4 fade-up">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-[#E3C2B0]" strokeWidth={1.5} />
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">Welcome gift</div>
                <div className="text-sm text-[#F7F5F2] mt-1">
                  Premium features unlocked for your first look — translate to your language, download in HD. <em>On us.</em>
                </div>
              </div>
            </div>
            <Link
              to="/subscription"
              className="text-xs uppercase tracking-[0.3em] bg-[#E3C2B0] text-[#0A0908] px-5 py-2.5 rounded-sm hover:bg-[#F0D4C5] whitespace-nowrap"
            >
              Keep Premium
            </Link>
          </div>
        )}
        <div className="flex items-center justify-between mb-12 flex-wrap gap-4">
          <Link to="/gallery" data-testid="look-back" className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2] flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All Looks
          </Link>
          <div className="flex items-center gap-4">
            <button onClick={download} data-testid="look-download" className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2] flex items-center gap-2">
              <Download className="w-4 h-4" strokeWidth={1.5} /> Save Image
            </button>
            <button onClick={remove} data-testid="look-delete" className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-red-300 flex items-center gap-2">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
            </button>
          </div>
        </div>

        {/* Preview + summary */}
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-7 fade-up">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-[#1A1921]">
              {previewSrc ? (
                <img src={`data:image/png;base64,${previewSrc}`} alt="Your look" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[#A8A6A1]">Preview unavailable</div>
              )}
              {working && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-[#E3C2B0] mx-auto mb-3 animate-spin" strokeWidth={1.5} />
                    <p className="text-xs uppercase tracking-[0.3em] text-[#F7F5F2]">Reimagining your look...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Hairstyle try-on and outfit color swap features removed Feb 2026 —
                the static "Hairstyle" breakdown card below still displays the AI's
                recommended hair for this look. */}
          </div>

          <div className="lg:col-span-5 fade-up delay-100">
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">{look.occasion}</span>
              <LanguageToggle current={language} onChange={translate} disabled={!canTranslate} />
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 font-light leading-tight">The Vision</h1>
            <p className="mt-8 text-[#F7F5F2]/90 leading-relaxed text-lg font-light">{view.summary}</p>
            {look.linked_outfit && (() => {
              const badgeLabel = look.linked_outfit.exact === false
                ? `Styled from your outfit (stylist's interpretation) · ${look.linked_outfit.title.slice(0, 50)}`
                : `Tried on from your outfit · ${look.linked_outfit.title.slice(0, 60)}`;
              const badgeCls =
                "mt-4 inline-flex items-center gap-2 rounded-full border border-[#E3C2B0]/30 bg-[#E3C2B0]/10 px-4 py-1.5 text-xs text-[#E3C2B0] transition-colors";
              return look.linked_outfit.url ? (
                <a
                  href={look.linked_outfit.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="look-linked-outfit"
                  className={`${badgeCls} hover:bg-[#E3C2B0]/20`}
                >
                  <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                  {badgeLabel}
                </a>
              ) : (
                <span data-testid="look-linked-outfit" className={badgeCls}>
                  <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                  {badgeLabel}
                </span>
              );
            })()}
          </div>
        </div>

        {/* Breakdown grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 mb-16">
          <Section title={localLabel(language, "Outfit")} data={view.outfit} testid="section-outfit" />
          <Section title={localLabel(language, "Makeup")} data={view.makeup} testid="section-makeup" />
          <Section title={localLabel(language, "Hairstyle")} data={view.hairstyle} testid="section-hair" />
        </div>

        {/* Accessories + Tips */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {view.accessories?.length > 0 && (
            <div className="bg-[#1A1921] border border-white/10 p-8">
              <span className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">{localLabel(language, "Accessories")}</span>
              <ul className="mt-6 space-y-3">
                {view.accessories.map((a, i) => (
                  <li key={i} className="font-serif text-xl text-[#F7F5F2] flex gap-3"><span className="text-[#E3C2B0] text-sm mt-2">·</span>{a}</li>
                ))}
              </ul>
            </div>
          )}
          {view.confidence_tips?.length > 0 && (
            <div className="bg-[#1A1921] border border-white/10 p-8">
              <span className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">{localLabel(language, "Confidence Notes")}</span>
              <ul className="mt-6 space-y-3">
                {view.confidence_tips.map((a, i) => (
                  <li key={i} className="text-[#F7F5F2]/90 flex gap-3">
                    <Sparkles className="w-3 h-3 text-[#E3C2B0] mt-1.5 flex-shrink-0" strokeWidth={1.5} />{a}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <ShopTheLook look={look} />
        <FeedbackCard lookId={look.id} />
      </div>
    </div>
  );
}

const localLabel = (lang, key) => {
  const map = {
    hindi: { Outfit: "पोशाक", Makeup: "मेकअप", Hairstyle: "हेयरस्टाइल", Accessories: "एक्सेसरीज़", "Confidence Notes": "आत्मविश्वास सुझाव" },
    tamil: { Outfit: "உடை", Makeup: "மேக்கப்", Hairstyle: "சிகை அலங்காரம்", Accessories: "துணைப் பொருட்கள்", "Confidence Notes": "தன்னம்பிக்கை குறிப்புகள்" },
    telugu: { Outfit: "దుస్తులు", Makeup: "మేకప్", Hairstyle: "హెయిర్ స్టైల్", Accessories: "ఆభరణాలు", "Confidence Notes": "ఆత్మవిశ్వాస చిట్కాలు" },
    bengali: { Outfit: "পোশাক", Makeup: "মেকআপ", Hairstyle: "হেয়ারস্টাইল", Accessories: "আনুষঙ্গিক", "Confidence Notes": "আত্মবিশ্বাসের পরামর্শ" },
    marathi: { Outfit: "पोशाख", Makeup: "मेकअप", Hairstyle: "केशरचना", Accessories: "उपसाधने", "Confidence Notes": "आत्मविश्वास सूचना" },
  };
  return map[lang]?.[key] || key;
};

const LanguageToggle = ({ current, onChange, disabled }) => (
  <div className="relative group">
    <button data-testid="language-toggle" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2]">
      <Languages className="w-3 h-3" strokeWidth={1.5} />
      {LANGUAGES.find((l) => l.id === current)?.label || "English"}
      {disabled && <Lock className="w-3 h-3 text-[#E3C2B0]" strokeWidth={1.5} />}
    </button>
    <div className="absolute right-0 top-full mt-2 hidden group-hover:block z-30 bg-[#1A1921] border border-white/10 rounded-sm overflow-hidden min-w-[160px]">
      {LANGUAGES.map((l) => (
        <button
          key={l.id}
          onClick={() => onChange(l.id)}
          data-testid={`lang-${l.id}`}
          className={`block w-full text-left px-4 py-2 text-xs hover:bg-white/5 transition-colors ${current === l.id ? "text-[#E3C2B0]" : "text-[#F7F5F2]"}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  </div>
);

const Section = ({ title, data, testid }) => {
  if (!data) return null;
  const entries = Object.entries(data).filter(([k]) => k !== "why" && k !== "color");
  return (
    <div data-testid={testid} className="bg-[#1A1921] p-8">
      <span className="text-xs uppercase tracking-[0.3em] text-[#E3C2B0]">{title}</span>
      <div className="mt-6 space-y-5">
        {entries.map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1] mb-1.5">{k}</div>
            <div className="font-serif text-xl text-[#F7F5F2] leading-snug">{typeof v === "string" ? v : JSON.stringify(v)}</div>
          </div>
        ))}
      </div>
      {data.why && (
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A6A1] mb-2">Why</div>
          <p className="text-sm text-[#F7F5F2]/80 italic font-light leading-relaxed">{data.why}</p>
        </div>
      )}
    </div>
  );
};

const ShopTheLook = ({ look }) => {
  const items = buildShoppingList(look);
  if (items.length === 0) return null;
  return (
    <div className="border-t border-white/5 pt-16 mb-8" data-testid="shop-search-links">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0] flex items-center gap-2">
            <ShoppingBag className="w-3 h-3" strokeWidth={1.5} /> Shop The Look
          </span>
          <h2 className="font-serif text-4xl mt-3 font-light">Find these pieces.</h2>
        </div>
        <p className="text-xs text-[#A8A6A1] max-w-xs">
          Search links to leading retailers — we may earn a small commission at no cost to you.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {items.map((item, i) => (
          <div key={i} className="bg-[#1A1921] p-6 group">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#E3C2B0]">{item.category}</span>
            <p className="font-serif text-lg mt-2 text-[#F7F5F2] leading-tight line-clamp-3">{item.label}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {item.stores.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`shop-${item.category.toLowerCase()}-${s.name.toLowerCase()}`}
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-[#E3C2B0] hover:text-[#E3C2B0] text-[10px] uppercase tracking-[0.3em] px-3 py-2 rounded-sm transition-colors"
                >
                  {s.name}
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
